<script setup lang="ts">
/**
 * Inline SVG frowning face icon drawn in the app's danger color
 * (`--color-tufte-danger`). Used on all error pages via `ErrorView` and
 * anywhere a "failure" illustration is needed.
 *
 * Props
 *   size — pixel number (applied to `width`/`height` attributes) or any CSS
 *          value string such as `'8rem'` or `'20vmin'` (applied via `style`).
 *          Defaults to `80` (px).
 */
const props = withDefaults(
  defineProps<{
    /** Size: number (pixels) or string (e.g. '8rem', '20vmin') for responsive sizing. */
    size?: number | string
  }>(),
  { size: 80 },
)

const isPixelSize = typeof props.size === 'number'
const sizeStyle = isPixelSize ? undefined : { width: props.size, height: props.size }
</script>

<template>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 100 100"
    :width="isPixelSize ? size : undefined"
    :height="isPixelSize ? size : undefined"
    :style="sizeStyle"
    aria-hidden="true"
    class="sad-smile-icon"
  >
    <!-- Face circle: transparent fill, danger stroke -->
    <circle
      cx="50"
      cy="50"
      r="45"
      fill="transparent"
      stroke="var(--color-tufte-danger)"
      stroke-width="2.5"
    />
    <!-- Sad eyes (curved down) -->
    <path
      d="M 30 38 Q 35 44 40 38"
      fill="none"
      stroke="var(--color-tufte-danger)"
      stroke-width="2.5"
      stroke-linecap="round"
    />
    <path
      d="M 60 38 Q 65 44 70 38"
      fill="none"
      stroke="var(--color-tufte-danger)"
      stroke-width="2.5"
      stroke-linecap="round"
    />
    <!-- Sad mouth (frown) -->
    <path
      d="M 32 62 Q 50 52 68 62"
      fill="none"
      stroke="var(--color-tufte-danger)"
      stroke-width="2.5"
      stroke-linecap="round"
    />
  </svg>
</template>

<style scoped>
.sad-smile-icon {
  display: block;
}
</style>
